"use client";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useViewerLanguage } from "@/lib/i18n";
import { fillSlots } from "@/lib/i18n-slots";
import { cn } from "@/lib/utils";
import { POSTHOG_CONSENT_EVENT } from "@/providers/posthog-provider";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useState } from "react";
import { TextAnimate } from "./ui/text-animate";

export const COOKIE_SETTINGS_EVENT = "cookie-settings-open";

const REMOVED_TRACKER_COOKIES = ["_clck", "_clsk"];

type ConsentPreferences = {
  necessary: true;
  analytics: boolean;
};

type CookieConsentVariant = "site" | "reader";

function cookieDomains(hostname: string): string[] {
  const parts = hostname.split(".");
  const domains: string[] = [];
  for (let i = 0; i < parts.length - 1; i++) {
    const domain = parts.slice(i).join(".");
    domains.push(domain, `.${domain}`);
  }
  return domains;
}

function removeTrackerCookies() {
  const present = document.cookie
    .split(";")
    .map((entry) => entry.split("=")[0]?.trim())
    .filter((name) => name && REMOVED_TRACKER_COOKIES.includes(name));
  if (present.length === 0) return;
  const domains = cookieDomains(window.location.hostname);
  for (const name of present) {
    document.cookie = `${name}=; Max-Age=0; path=/`;
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
    }
  }
}

export default function CookieConsent({
  variant = "site",
}: {
  variant?: CookieConsentVariant;
}) {
  const reader = variant === "reader";
  const { t } = useViewerLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true,
    analytics: false,
  });

  useEffect(() => {
    removeTrackerCookies();
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      setTimeout(() => setIsVisible(true), 1000);
      return;
    }
    const savedPreferences = localStorage.getItem("cookie-preferences");
    if (savedPreferences) {
      const parsed = JSON.parse(savedPreferences) as { analytics?: boolean };
      setPreferences({ necessary: true, analytics: !!parsed.analytics });
    }
  }, []);

  useEffect(() => {
    const open = () => setIsModalOpen(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, open);
  }, []);

  const persistAndApply = (
    consentKind: "accepted" | "custom" | "declined",
    prefs: ConsentPreferences,
  ) => {
    localStorage.setItem("cookie-consent", consentKind);
    localStorage.setItem("cookie-preferences", JSON.stringify(prefs));
    setIsVisible(false);
    setIsModalOpen(false);
    window.dispatchEvent(new Event(POSTHOG_CONSENT_EVENT));
  };

  const handleAccept = () => {
    const prefs: ConsentPreferences = {
      necessary: true,
      analytics: true,
    };
    setPreferences(prefs);
    persistAndApply("accepted", prefs);
  };

  const handleSavePreferences = () => {
    persistAndApply("custom", preferences);
  };

  const handleDecline = () => {
    const prefs: ConsentPreferences = {
      necessary: true,
      analytics: false,
    };
    setPreferences(prefs);
    persistAndApply("declined", prefs);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <AnimatePresence>
        {isVisible && !isModalOpen && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className={cn(
              "fixed left-4 right-4 mx-auto max-w-[90rem]",
              reader
                ? "bottom-4 z-[1100] text-neutral-900 dark:text-neutral-100"
                : "bottom-16 z-[60] lg:bottom-20",
            )}
          >
            <div
              className={cn(
                "bg-white/20 backdrop-blur-sm border border-neutral-200 shadow-2xl/8 p-4 flex flex-col items-center gap-4 lg:flex-row lg:gap-6",
                reader &&
                  "bg-white/80 dark:border-neutral-800 dark:bg-neutral-900/80",
              )}
            >
              <div className="flex flex-row gap-2 items-center">
                <p>{t("We respect your privacy")}</p>
              </div>
              <div className="flex flex-col gap-4 items-center self-stretch lg:flex-row lg:gap-2">
                <TextAnimate
                  className="text-sm font-light leading-relaxed"
                  animation="blurInUp"
                  by="word"
                  delay={0}
                >
                  {t(
                    "We use cookies to improve our website and analyze how you use it.",
                  )}
                </TextAnimate>
                <button
                  onClick={openModal}
                  className="underline text-neutral-600 cursor-pointer text-sm font-light hover:text-neutral-900 transition-colors whitespace-nowrap"
                >
                  {t("Customize settings")}
                </button>
              </div>

              <div className="flex w-full flex-1 flex-row items-center justify-end gap-3 lg:w-auto">
                <Button
                  onClick={handleDecline}
                  variant="outline"
                  size="sm"
                  className="flex-1 lg:flex-none"
                >
                  {t("Decline")}
                </Button>
                <Button
                  onClick={handleAccept}
                  size="sm"
                  className="flex-1 text-white lg:flex-none"
                >
                  {t("Accept all")}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={cn(
                "fixed inset-0 bg-neutral-800/50 backdrop-blur-sm",
                reader ? "z-[1200]" : "z-[120]",
              )}
              onClick={() => setIsModalOpen(false)}
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className={cn(
                "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[calc(100%-2rem)] max-h-[80vh] overflow-y-auto lg:max-w-2xl lg:max-h-[90vh]",
                reader
                  ? "z-[1210] text-neutral-900 dark:text-neutral-100"
                  : "z-[130]",
              )}
              data-lenis-prevent
            >
              <div
                className={cn(
                  "bg-white border border-neutral-200 shadow-2xl p-6 lg:p-8",
                  reader &&
                    "dark:border-neutral-800 dark:bg-neutral-900 dark:[&_p]:text-neutral-400",
                )}
              >
                <div className="flex flex-col gap-2 mb-3 lg:mb-4">
                  <div className="flex flex-row items-start gap-4">
                    <h2 className="text-2xl font-medium leading-none mb-2 lg:text-3xl">
                      {t("Cookie settings")}
                    </h2>
                  </div>
                  <p className="text-sm font-light text-neutral-600 mt-2 lg:mt-4">
                    {t(
                      "We use cookies to improve your visit and analyze our reach. You decide which categories to allow. You can change your choice here at any time.",
                    )}
                  </p>
                </div>

                <Separator className="mb-4 lg:mb-6" />

                <div className="flex flex-col gap-4 lg:gap-6">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="flex flex-row items-start justify-between gap-4"
                  >
                    <div className="flex-1">
                      <h3 className="text-base font-medium mb-1 lg:mb-2">
                        {t("Necessary cookies")}
                      </h3>
                      <p className="text-sm font-light text-neutral-600 leading-relaxed">
                        {t(
                          "These cookies are technically required for the website to function — session management, security, storing your cookie choice. No tracking data is stored. Legal basis: Art. 6 (1) lit. f GDPR (legitimate interest).",
                        )}
                      </p>
                    </div>
                    <Switch
                      checked={preferences.necessary}
                      disabled
                      className="mt-1"
                    />
                  </motion.div>

                  <Separator />

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="flex flex-row items-start justify-between gap-4"
                  >
                    <div className="flex-1">
                      <h3 className="text-base font-medium mb-1 lg:mb-2">
                        {t("Analytics cookies")}
                      </h3>
                      <p className="text-sm font-light text-neutral-600 leading-relaxed mb-2">
                        {t(
                          "Help us find bugs and the places where people get stuck — page views, clicks, click paths, heatmaps, errors, performance and session recordings with input fields masked. No advertising. Legal basis: Art. 6 (1) lit. a GDPR (consent).",
                        )}
                      </p>
                      <p className="text-xs font-light text-neutral-500">
                        {t("Service used: PostHog (hosted in the EU).")}
                      </p>
                    </div>
                    <Switch
                      checked={preferences.analytics}
                      onCheckedChange={(checked) =>
                        setPreferences({
                          ...preferences,
                          analytics: checked,
                        })
                      }
                      className="mt-1"
                    />
                  </motion.div>

                  <Separator />

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <p className="text-xs font-light text-neutral-500 leading-relaxed">
                      {fillSlots(
                        t("More information on how we process data is in our {privacy}."),
                        {
                          privacy: (
                            <Link
                              href="/privacy"
                              className="text-primary underline hover:no-underline"
                            >
                              {t("privacy policy")}
                            </Link>
                          ),
                        },
                      )}
                    </p>
                  </motion.div>
                </div>

                <div className="flex flex-col gap-4 mt-6 lg:flex-row lg:mt-8">
                  <Button
                    onClick={handleDecline}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    {t("Decline all")}
                  </Button>
                  <Button
                    onClick={handleSavePreferences}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    {t("Save selection")}
                  </Button>
                  <Button
                    onClick={handleAccept}
                    size="sm"
                    className="flex-1  text-white "
                  >
                    {t("Accept all")}
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
